import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { DailyScheduleConfig } from '../daily-schedule/daily-schedule.interface';

interface WorkExceptionDetailResponse {
  workExceptionId: string;
  companiesRecordId?: string;
  employeeId?: string;
  date?: string;
  reason?: string;
  isActive?: boolean;
  override?: {
    dayType?: 'work' | 'rest' | string;
    dailyScheduleId?: string | null;
    journeyId?: string | null;
  };
}

interface DailySchedulesResponse {
  data?: DailyScheduleConfig[];
  items?: DailyScheduleConfig[];
  results?: DailyScheduleConfig[];
  dailySchedules?: DailyScheduleConfig[];
}

export interface WorkExceptionDialogData {
  workExceptionId?: string | null;
  companiesRecordId: string;
  employeeId: string;
  employeeLabel: string;
  date: string;
  currentDayType: 'work' | 'rest' | 'neutral';
  currentScheduleLabel: string;
}

export interface WorkExceptionDialogResult {
  refresh: boolean;
}

@Component({
  selector: 'app-work-exception-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './work-exception-dialog.component.html',
  styleUrl: './work-exception-dialog.component.scss',
})
export class WorkExceptionDialogComponent {
  private readonly restOptionValue = '__rest__';
  private readonly dialogRef = inject(MatDialogRef<WorkExceptionDialogComponent>);
  private readonly apiService = inject(AuthorizedApiService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  readonly data = inject<WorkExceptionDialogData>(MAT_DIALOG_DATA);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  schedules: DailyScheduleConfig[] = [];
  dailyScheduleId = '';
  reason = '';
  isActive = true;

  constructor() {
    void this.initialize();
  }

  get isEditMode(): boolean {
    return Boolean(this.data.workExceptionId);
  }

  get isWorkDay(): boolean {
    return this.dailyScheduleId !== this.restOptionValue;
  }

  get isRestDay(): boolean {
    return this.dailyScheduleId === this.restOptionValue;
  }

  get employeeName(): string {
    return this.employeeLabelParts.name;
  }

  get employeeNumber(): string {
    return this.employeeLabelParts.number;
  }

  get formattedExceptionDate(): string {
    return new Intl.DateTimeFormat(this.currentLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(this.parseLocalDate(this.data.date));
  }

  get exceptionWeekday(): string {
    const weekday = new Intl.DateTimeFormat(this.currentLocale, { weekday: 'long' }).format(
      this.parseLocalDate(this.data.date)
    );
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }

  get reasonLength(): number {
    return this.reason.length;
  }

  get dialogTitle(): string {
    return this.isEditMode
      ? this.t('workExceptionsCatalog.detail.editTitle')
      : this.t('workExceptionsCatalog.detail.createTitle');
  }

  get dialogDescription(): string {
    return this.isEditMode
      ? this.t('workExceptionsCatalog.detail.editDescription')
      : this.t('workExceptionsCatalog.detail.createDescription');
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  selectDayType(dayType: 'work' | 'rest'): void {
    if (this.isSaving || this.isDeleting) {
      return;
    }

    if (dayType === 'rest') {
      this.dailyScheduleId = this.restOptionValue;
      return;
    }

    if (this.isRestDay) {
      this.dailyScheduleId = '';
    }
  }

  close(): void {
    if (this.isSaving || this.isDeleting) {
      return;
    }
    this.dialogRef.close();
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isEditMode && this.data.workExceptionId) {
        const response = await firstValueFrom(
          this.apiService.patch<WorkExceptionDetailResponse | { deleted?: boolean }>(
            `labor-config/work-exceptions/${this.data.workExceptionId}`,
            this.buildPayload(true)
          )
        );

        if ((response as { deleted?: boolean })?.deleted) {
          this.showNotification('snackbar-success', this.t('workExceptionsCatalog.notifications.deletedByRedundancy'), 'bottom', 'center');
          this.dialogRef.close({ refresh: true } satisfies WorkExceptionDialogResult);
          return;
        }

        this.showNotification('snackbar-success', this.t('workExceptionsCatalog.notifications.updated'), 'bottom', 'center');
      } else {
        await firstValueFrom(this.apiService.post('labor-config/work-exceptions', this.buildPayload(false)));
        this.showNotification('snackbar-success', this.t('workExceptionsCatalog.notifications.created'), 'bottom', 'center');
      }

      this.dialogRef.close({ refresh: true } satisfies WorkExceptionDialogResult);
    } catch (error) {
      console.error('Error saving work exception from calendar', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workExceptionsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  async deleteCurrent(): Promise<void> {
    if (!this.data.workExceptionId) {
      return;
    }

    this.isDeleting = true;
    try {
      await firstValueFrom(this.apiService.delete(`labor-config/work-exceptions/${this.data.workExceptionId}`));
      this.showNotification('snackbar-success', this.t('workExceptionsCatalog.notifications.deleted'), 'bottom', 'center');
      this.dialogRef.close({ refresh: true } satisfies WorkExceptionDialogResult);
    } catch (error) {
      console.error('Error deleting work exception from calendar', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workExceptionsCatalog.notifications.deleteError')),
        'bottom',
        'center'
      );
    } finally {
      this.isDeleting = false;
    }
  }

  getScheduleName(scheduleId: string): string {
    return this.schedules.find((schedule) => schedule.dailyScheduleId === scheduleId)?.name || scheduleId;
  }

  private async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      const schedulesResponse = await firstValueFrom(
        this.apiService.get<DailyScheduleConfig[] | DailySchedulesResponse>('labor-config/daily-schedules', {
          params: this.data.companiesRecordId ? { companiesRecordId: this.data.companiesRecordId } : undefined,
        })
      );
      this.schedules = this.extractDailySchedules(schedulesResponse)
        .filter((schedule) => schedule.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));

      if (this.isEditMode && this.data.workExceptionId) {
        const detail = await firstValueFrom(
          this.apiService.get<WorkExceptionDetailResponse>(`labor-config/work-exceptions/${this.data.workExceptionId}`)
        );
        this.dailyScheduleId =
          detail.override?.dayType === 'rest' ? this.restOptionValue : detail.override?.dailyScheduleId || '';
        this.reason = detail.reason || '';
        this.isActive = detail.isActive !== false;
      } else {
        const currentSchedule = this.schedules.find(
          (schedule) => this.normalizeText(schedule.name) === this.normalizeText(this.data.currentScheduleLabel)
        );
        this.dailyScheduleId =
          this.data.currentDayType === 'rest' ? this.restOptionValue : currentSchedule?.dailyScheduleId || '';
        this.reason = '';
        this.isActive = true;
      }
    } catch (error) {
      console.error('Error loading work exception dialog', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workExceptionsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      this.dialogRef.close();
    } finally {
      this.isLoading = false;
    }
  }

  private validateForm(): string | null {
    if (!this.data.companiesRecordId) {
      return this.t('orgUnitsCatalog.validationCompany');
    }
    if (!this.data.employeeId) {
      return this.t('workExceptionsCatalog.notifications.validationEmployee');
    }
    if (!this.data.date) {
      return this.t('workExceptionsCatalog.notifications.validationDate');
    }
    if (!this.dailyScheduleId) {
      return this.t('workExceptionsCatalog.notifications.validationDailySchedule');
    }
    return null;
  }

  private buildPayload(includeActive: boolean): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      companiesRecordId: this.data.companiesRecordId,
      employeeId: this.data.employeeId,
      date: this.data.date,
      override: {
        dayType: this.isRestDay ? 'rest' : 'work',
        ...(this.isWorkDay && this.dailyScheduleId ? { dailyScheduleId: this.dailyScheduleId } : {}),
      },
      reason: this.reason.trim(),
    };

    if (includeActive) {
      payload['isActive'] = this.isActive;
    }

    return payload;
  }

  private extractDailySchedules(response: DailyScheduleConfig[] | DailySchedulesResponse | null | undefined): DailyScheduleConfig[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response?.data || response?.items || response?.results || response?.dailySchedules || [];
  }

  private get employeeLabelParts(): { name: string; number: string } {
    const label = (this.data.employeeLabel || '').trim();
    const separator = label.lastIndexOf(' · ');
    if (separator < 0) {
      return { name: label || '-', number: '' };
    }
    return {
      name: label.slice(0, separator).trim() || '-',
      number: label.slice(separator + 3).trim(),
    };
  }

  private get currentLocale(): string {
    const language = (this.translate.currentLang || this.translate.defaultLang || 'es').toLowerCase();
    return language === 'en' || language === 'us' ? 'en-US' : 'es-MX';
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private normalizeText(value: string | null | undefined): string {
    return (value || '').trim().toLocaleLowerCase(this.currentLocale);
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
